import classNames from 'classnames';
import {
    IconFaceVerified,
    IconAgeEstimation,
    IconDocument,
} from '@yoti/ui-kit-components-react/globals';

import React from 'react';
import { useAgeRangeInterval } from '@hooks';
import { RequestData } from '@hooks/usePredict';
import { Liveness, LivenessOutput, PredictionResponse } from '@models/prediction';
import Config from '@utils/config';
import Response from '@components/result';
import { AgeDisplayer } from './age-displayer/AgeDisplayer';
import styles from './ageAntispoofingResponse.module.scss';

interface Props {
    responseData?: PredictionResponse;
    request?: RequestData;
}

const AgeAntispoofingResponse: React.FC<Props> = (props) => {
    const { responseData, request } = props;

    const { prediction, age, stDev, errorCode, errorMessage, ageCheck } = responseData || {};
    const interval = useAgeRangeInterval();

    const livenessCheck = () => {
        switch (prediction) {
            case Liveness.Real:
                return (
                    <span
                        data-qa="liveness-result"
                        className={classNames(
                            styles['response__text'],
                            styles['response__text--passed'],
                        )}
                    >
                        {LivenessOutput(prediction)}
                    </span>
                );
            case Liveness.Fake:
                return (
                    <span
                        data-qa="liveness-result"
                        className={classNames(
                            styles['response__text'],
                            styles['response__text--failed'],
                        )}
                    >
                        {LivenessOutput(prediction)}
                    </span>
                );
            case Liveness.Undetermined:
                return (
                    <span
                        data-qa="liveness-result"
                        className={classNames(
                            styles['response__text'],
                            styles['response__text--undetermined'],
                        )}
                    >
                        {LivenessOutput(prediction)}
                    </span>
                );
            default:
                return undefined;
        }
    };

    const ageChecking = () => {
        switch (ageCheck) {
            case Liveness.Pass:
                return (
                    <span
                        data-qa="liveness-result"
                        className={classNames(
                            styles['response__text'],
                            styles['response__text--passed'],
                        )}
                    >
                        {LivenessOutput(ageCheck)}
                    </span>
                );
            case Liveness.Fail:
                return (
                    <span
                        data-qa="liveness-result"
                        className={classNames(
                            styles['response__text'],
                            styles['response__text--failed'],
                        )}
                    >
                        {LivenessOutput(ageCheck)}
                    </span>
                );
            default:
                return undefined;
        }
    };

    const liveness = livenessCheck();
    const agePredictionIsValid = stDev !== undefined && stDev <= Config.stdDevThreshold;
    const ageVerify = ageChecking();

    return (
        <Response
            errorCode={errorCode}
            errorMessage={errorMessage}
            data-qa="age-antispoofing-response"
        >
            {liveness && (
                <>
                    <IconFaceVerified className={styles['response__icon']} />
                    <span
                        className={classNames(
                            styles['response__text'],
                            styles['response__text--bold'],
                        )}
                    >{`Liveness check: `}</span>
                    {liveness}
                </>
            )}
            {age && (
                <AgeDisplayer
                    agePredictionIsValid={agePredictionIsValid}
                    age={age}
                    interval={interval}
                    data-qa="age-info"
                />
            )}
            {ageCheck && (
                <>
                    <IconAgeEstimation className={styles['response__icon']} />
                    <div>
                        <span
                            className={classNames(
                                styles['response__text'],
                                styles['response__text--bold'],
                            )}
                        >
                            Age {request?.operator?.toLowerCase()} {request?.threshold}:
                        </span>
                        {ageVerify}
                    </div>
                </>
            )}
            {responseData?.logId && (
                <>
                    <IconDocument className={styles['response__icon']} />
                    <span
                        className={classNames(
                            styles['response__text'],
                            styles['response__text--bold'],
                        )}
                    >{`Log ID: `}</span>
                    {responseData.logId}
                </>
            )}
            {age && !agePredictionIsValid && (
                <div
                    data-qa="undetermined-age-info"
                    className={classNames(
                        styles['response__age-accuracy-error'],
                        styles['response__text--secondary'],
                    )}
                >
                    * Please try again.
                </div>
            )}
        </Response>
    );
};

export default AgeAntispoofingResponse;
